import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventEndpoints } from '../api';
import { seasonEndpoints } from '../api-seasons';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Field, Panel } from '../ui';
import { INPUT, Tabs } from './event/shared';
import { BuildPanel } from './season/BuildPanel';
import { PricesPanel } from './season/PricesPanel';
import { PublishPanel } from './season/PublishPanel';

/**
 * Season tools (D-371): one event per game, made to feel like one season.
 *
 * Three jobs, in the order they are done: **Build** the games from the
 * schedule by copying a template game, check and change their **Prices** on
 * one grid, then **Publish** them — prices activated, inventory set up, on
 * sale. Seasons themselves are created on an event's Collections tab (D-169);
 * a season ticket covering every game is set up under Packages.
 */

type Tab = 'build' | 'prices' | 'publish';

export default function SeasonsPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const seasons = useQuery({ queryKey: ['collections', 'seasons'], queryFn: () => eventEndpoints.seasons() });
  const [seasonId, setSeasonId] = useState('');
  const [tab, setTab] = useState<Tab>('build');
  const games = useQuery({ queryKey: ['season-games', seasonId], queryFn: () => seasonEndpoints.games(seasonId), enabled: Boolean(seasonId) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['season-games', seasonId] });
    void queryClient.invalidateQueries({ queryKey: ['season-prices', seasonId] });
    void queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  if (!can('events.manage')) return <NotPermitted permission="events.manage" />;
  const season = (seasons.data?.seasons ?? []).find((row) => row.id === seasonId) ?? null;
  const list = games.data?.games ?? [];
  const onSale = list.filter((game) => game.performance_status === 'ON_SALE').length;

  return (
    <div className="space-y-4">
      <Panel title="Seasons" description="Build a season’s games from the schedule, price them together, and publish them in bulk. Every game stays its own event and can still be edited by hand.">
        <div className="flex flex-wrap items-end gap-4 px-5 py-4">
          <Field label="Season" htmlFor="season-pick" hint={seasons.data && seasons.data.seasons.length === 0 ? 'No seasons yet. Create one on any event’s Collections tab.' : undefined}>
            <select id="season-pick" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className={`w-72 ${INPUT}`}>
              <option value="">Choose a season…</option>
              {(seasons.data?.seasons ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name} ({row.starts_on} to {row.ends_on})
                </option>
              ))}
            </select>
          </Field>
          {season && games.data ? (
            <p className="text-sm text-ink-600">
              {list.length} {list.length === 1 ? 'game' : 'games'}, {list.filter((game) => game.status === 'PUBLISHED').length} published, {onSale} on sale
            </p>
          ) : null}
        </div>
      </Panel>

      {season ? (
        <>
          <Tabs<Tab>
            tabs={[
              { id: 'build', label: 'Build from schedule' },
              { id: 'prices', label: 'Prices', badge: list.length },
              { id: 'publish', label: 'Publish', badge: list.length - onSale },
            ]}
            active={tab}
            onChange={setTab}
          />
          {tab === 'build' ? <BuildPanel season={season} onBuilt={refresh} /> : null}
          {tab === 'prices' ? <PricesPanel seasonId={season.id} onChanged={refresh} /> : null}
          {tab === 'publish' ? <PublishPanel seasonId={season.id} games={list} onChanged={refresh} /> : null}
        </>
      ) : null}
    </div>
  );
}
